import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save,
  Building2,
  Users,
  CreditCard,
  Loader2,
  AlertTriangle,
  Plus,
  X,
  Star,
  FolderOpen,
  ExternalLink,
  Trash2,
  DollarSign,
  FileText,
  Globe,
  Eye,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

declare global {
  interface Window {
    Dropbox: {
      choose(options: {
        success: (files: Array<{ name: string; link: string }>) => void;
        linkType: 'preview' | 'direct';
        multiselect: boolean;
        folderselect: boolean;
      }): void;
    };
  }
}
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { api } from '../../core/apiClient';
import { SHIPPING_METHODS } from './constants';
import { formatCurrency, formatDate } from '../../lib/utils';
import { SupplierEvaluationTab } from './components/SupplierEvaluationTab';
import { SupplierIncidentsTab } from './components/SupplierIncidentsTab';

interface SupplierContact {
  name: string;
  position?: string;
  phone?: string;
  email?: string;
  isPrimary?: boolean;
}

interface SupplierFormData {
  code: string;
  name: string;
  tradeName: string;
  cif: string;
  address: string;
  city: string;
  postalCode: string;
  province: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  contacts: SupplierContact[];
  bankAccount: string;
  supplierIban: string;
  paymentTerms: number;
  paymentMethod: string;
  notes: string;
  shippingMethod: string;
  horario: string;
  dropboxUrl: string;
  activitatId: number | null;
  supplierType: string;
}

interface PurchaseOrder {
  orderId: string;
  orderNumber: string;
  otNumber?: string;
  supplierName: string;
  projectNumber?: string;
  status: 'DRAFT' | 'SENT' | 'PARTIALLY_RECEIVED' | 'RECEIVED' | 'CANCELLED';
  orderDate: string;
  expectedDeliveryDate?: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  currency: string;
  linesCount: number;
  receivedLinesCount: number;
}

const statusConfig: Record<string, { label: string; color: string }> = {
  DRAFT:              { label: 'Esborrany',     color: 'bg-gray-100 text-gray-800' },
  SENT:               { label: 'Enviat',         color: 'bg-indigo-100 text-indigo-800' },
  PARTIALLY_RECEIVED: { label: 'Rebut Parcial',  color: 'bg-orange-100 text-orange-800' },
  RECEIVED:           { label: 'Rebut',          color: 'bg-green-100 text-green-800' },
  CANCELLED:          { label: 'Cancel·lat',     color: 'bg-red-100 text-red-800' },
};

type TabValue = 'dades' | 'comandes' | 'incidencies' | 'valoracio';

const initialFormData: SupplierFormData = {
  code: '',
  name: '',
  tradeName: '',
  cif: '',
  address: '',
  city: '',
  postalCode: '',
  province: '',
  country: 'CAT',
  phone: '',
  email: '',
  website: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  contacts: [],
  bankAccount: '',
  supplierIban: '',
  paymentTerms: 30,
  paymentMethod: '',
  notes: '',
  shippingMethod: '',
  horario: '',
  dropboxUrl: '',
  activitatId: null,
  supplierType: 'HABITUAL',
};

const countryCodes = [
  { value: 'CAT', label: 'Catalunya' },
  { value: 'ES',  label: 'Espanya' },
  { value: 'FR',  label: 'França' },
  { value: 'PT',  label: 'Portugal' },
  { value: 'IT',  label: 'Itàlia' },
  { value: 'DE',  label: 'Alemanya' },
  { value: 'BE',  label: 'Bèlgica' },
  { value: 'NL',  label: 'Països Baixos' },
  { value: 'AT',  label: 'Àustria' },
  { value: 'PL',  label: 'Polònia' },
  { value: 'CZ',  label: 'República Txeca' },
  { value: 'SK',  label: 'Eslovàquia' },
  { value: 'HU',  label: 'Hongria' },
  { value: 'RO',  label: 'Romania' },
  { value: 'BG',  label: 'Bulgària' },
  { value: 'GR',  label: 'Grècia' },
  { value: 'GB',  label: 'Reino Unido' },
  { value: 'IE',  label: 'Irlanda' },
  { value: 'DK',  label: 'Dinamarca' },
  { value: 'SE',  label: 'Suècia' },
  { value: 'NO',  label: 'Noruega' },
  { value: 'FI',  label: 'Finlàndia' },
  { value: 'LU',  label: 'Luxemburg' },
  { value: 'MT',  label: 'Malta' },
  { value: 'CY',  label: 'Xipre' },
  { value: 'HR',  label: 'Croàcia' },
  { value: 'SI',  label: 'Eslovènia' },
];

export default function SupplierForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData]   = useState<SupplierFormData>(initialFormData);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [activitats, setActivitats] = useState<Array<{ id: number; name: string }>>([]);
  const [, setLoadingActivitats]  = useState(false);
  const [bankAccounts, setBankAccounts] = useState<Array<{ id: string; iban: string; bankName: string | null; accountHolder: string | null; isDefault: number }>>([]);
  const [metodesPagament, setMetodesPagament] = useState<{ id: number; name: string }[]>([]);
  const [dropboxReady, setDropboxReady] = useState(false);

  // Edit-mode extras
  const [activeTab, setActiveTab]           = useState<TabValue>('dades');
  const [condicionsOpen, setCondicionsOpen] = useState(false);
  const [contactsOpen, setContactsOpen]     = useState(false);
  const [orders, setOrders]                 = useState<PurchaseOrder[]>([]);
  const [loadingOrders, setLoadingOrders]   = useState(false);
  const [totalPurchasesAmount, setTotalPurchasesAmount] = useState(0);
  const [latestEvaluation, setLatestEvaluation] = useState<any>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting]             = useState(false);

  // Dropbox SDK
  useEffect(() => {
    api.get<{ success: boolean; data: { appKey: string } }>('/admin/settings/dropbox')
      .then((r) => {
        const key = r.data?.appKey;
        if (!key) return;
        if (document.getElementById('dropboxjs')) { setDropboxReady(true); return; }
        const script = document.createElement('script');
        script.id = 'dropboxjs';
        script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
        script.setAttribute('data-app-key', key);
        script.onload = () => setDropboxReady(true);
        document.head.appendChild(script);
      })
      .catch(() => {});
  }, []);

  const handleDropboxChoose = () => {
    if (!window.Dropbox) return;
    window.Dropbox.choose({
      success: (files) => {
        const file = files[0];
        setFormData(prev => ({ ...prev, dropboxUrl: JSON.stringify({ name: file.name, link: file.link }) }));
      },
      linkType: 'preview',
      multiselect: false,
      folderselect: true,
    });
  };

  // Metodes de pagament (catàleg Admin > Comptabilitat)
  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; name: string; isActive: number }[] }>('/admin/metodes-pagament')
      .then((r) => {
        const list = (r.data ?? []).filter((m) => m.isActive);
        setMetodesPagament(list);
        // Proveïdor nou: Mètode de pagament per defecte "Transferència bancària".
        if (!isEditing) {
          const transferencia = list.find((m) => /transfer[eè]ncia/i.test(m.name));
          if (transferencia) setFormData((prev) => ({ ...prev, paymentMethod: prev.paymentMethod || transferencia.name }));
        }
      })
      .catch(() => {});
  }, [isEditing]);

  // Activitats + comptes bancaris
  useEffect(() => {
    const fetchActivitats = async () => {
      setLoadingActivitats(true);
      try {
        const r = await api.get<{ success: boolean; data: Array<{ id: number; name: string }> }>('/admin/activitats');
        setActivitats(r.data || []);
      } catch { /* silent */ }
      finally { setLoadingActivitats(false); }
    };
    const fetchBankAccounts = async () => {
      try {
        const r = await api.get<{ success: boolean; data: typeof bankAccounts }>('/admin/settings/bank-accounts');
        setBankAccounts(r.data || []);
      } catch { setBankAccounts([]); }
    };
    fetchActivitats();
    fetchBankAccounts();
  }, []);

  // Carregar proveïdor
  useEffect(() => {
    if (!isEditing || !id) return;
    const fetchSupplier = async () => {
      setLoading(true);
      try {
        const r = await api.get<{ success: boolean; data: any }>(`/suppliers/${id}`);
        const s = r.data;
        setFormData({
          code:          s.code          || '',
          name:          s.name          || '',
          tradeName:     s.tradeName     || '',
          cif:           s.cif           || '',
          address:       s.address       || '',
          city:          s.city          || '',
          postalCode:    s.postalCode    || '',
          province:      s.province      || '',
          country:       s.country       || 'ES',
          phone:         formatPhone(s.phone          || ''),
          email:         s.email         || '',
          website:       s.website       || '',
          contactName:   s.contactName   || '',
          contactPhone:  formatPhone(s.contactPhone   || ''),
          contactEmail:  s.contactEmail  || '',
          contacts:      (s.contacts || []).map((c: SupplierContact) => ({ ...c, phone: formatPhone(c.phone || '') })),
          bankAccount:   s.bankAccount   || '',
          supplierIban:  s.supplierIban  || '',
          paymentTerms:  s.paymentTerms  || 30,
          paymentMethod: s.paymentMethod || '',
          notes:         s.notes         || '',
          shippingMethod:s.shippingMethod|| '',
          horario:       s.horario       || '',
          dropboxUrl:    s.dropboxUrl    || '',
          activitatId:   s.activitatId   || null,
          supplierType:  s.supplierType  || 'HABITUAL',
        });
      } catch { setError('Error en carregar el proveïdor'); }
      finally { setLoading(false); }
    };
    fetchSupplier();
  }, [id, isEditing]);

  // Comandes del proveïdor
  useEffect(() => {
    if (!isEditing || !id) return;
    const fetchOrders = async () => {
      setLoadingOrders(true);
      try {
        const r = await api.get<{ success: boolean; data: PurchaseOrder[] }>(`/purchase-orders?supplierId=${id}`);
        const fetched = r.data || [];
        setOrders(fetched);
        setTotalPurchasesAmount(fetched.reduce((sum, o) => sum + o.total, 0));
      } catch { /* silent */ }
      finally { setLoadingOrders(false); }
    };
    fetchOrders();
  }, [id, isEditing]);

  // Última avaluació
  useEffect(() => {
    if (!isEditing || !id) return;
    api.get<{ success: boolean; data: any[] }>(`/supplier-evaluations/${id}`)
      .then(r => setLatestEvaluation((r.data || [])[0] || null))
      .catch(() => {});
  }, [id, isEditing]);

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    return digits.replace(/(\d{3})(?=\d)/g, '$1 ');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseFloat(value) || 0 : value }));
  };

  const handleContactChange = (index: number, field: keyof SupplierContact, value: string | boolean) => {
    const updated = [...formData.contacts];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, contacts: updated }));
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, { name: '', position: '', phone: '', email: '', isPrimary: prev.contacts.length === 0 }],
    }));
  };

  const removeContact = (index: number) => {
    const updated = formData.contacts.filter((_, i) => i !== index);
    if (formData.contacts[index].isPrimary && updated.length > 0) updated[0].isPrimary = true;
    setFormData(prev => ({ ...prev, contacts: updated }));
  };

  const setPrimaryContact = (index: number) => {
    setFormData(prev => ({ ...prev, contacts: prev.contacts.map((c, i) => ({ ...c, isPrimary: i === index })) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!formData.name?.trim()) { setError('El nom del proveïdor es obligatori'); return; }
      if (!formData.cif?.trim())  { setError('El CIF es obligatori'); return; }

      const nullableText = (v?: string) => v?.trim() || null;
      const payload: Record<string, any> = {
        name:          formData.name.trim(),
        cif:           formData.cif.trim(),
        tradeName:     nullableText(formData.tradeName),
        address:       nullableText(formData.address),
        city:          nullableText(formData.city),
        postalCode:    nullableText(formData.postalCode),
        province:      nullableText(formData.province),
        country:       nullableText(formData.country),
        phone:         nullableText(formData.phone),
        email:         nullableText(formData.email),
        website:       nullableText(formData.website),
        contactName:   nullableText(formData.contactName),
        contactPhone:  nullableText(formData.contactPhone),
        contactEmail:  nullableText(formData.contactEmail),
        contacts:      formData.contacts.length > 0 ? formData.contacts : null,
        bankAccount:   nullableText(formData.bankAccount),
        supplierIban:  nullableText(formData.supplierIban),
        paymentTerms:  formData.paymentTerms || null,
        paymentMethod: formData.paymentMethod || undefined,
        notes:         nullableText(formData.notes),
        shippingMethod:nullableText(formData.shippingMethod),
        horario:       nullableText(formData.horario),
        dropboxUrl:    nullableText(formData.dropboxUrl),
        activitatId:   formData.activitatId || null,
        supplierType:  formData.supplierType || 'HABITUAL',
      };
      if (formData.code) payload.code = formData.code;

      if (isEditing && id) {
        await api.put(`/suppliers/${id}`, payload);
      } else {
        await api.post('/suppliers', payload);
      }
      localStorage.setItem('compras-activeTab', 'suppliers');
      navigate('/compres');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Error en guardar el proveïdor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await api.delete(`/suppliers/${id}`);
      navigate('/compres');
    } catch { setError('Error en eliminar el proveïdor'); }
    finally { setDeleting(false); setShowDeleteDialog(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const tabCls = (tab: TabValue) =>
    `py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
      activeTab === tab
        ? 'border-primary text-primary'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`;

  return (
    <div className="space-y-4">
      {/* Capçalera sticky — fons blanc sòlid i sense icona de tornar, ja que
          "Cancel·lar" a la dreta (sempre visible, no nomes a la pestanya
          "Dades") fa la mateixa funció (estàndard de totes les fitxes de
          l'ERP, 2026-11). */}
      <div className="sticky top-0 z-20 bg-white border-b flex items-center justify-between gap-4 py-3 -mx-4 px-4 md:-mx-6 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground truncate">
              {isEditing ? (formData.name || 'Editar Proveïdor') : 'Nou Proveïdor'}
            </h1>
            {isEditing && formData.code && (
              <p className="text-xs text-muted-foreground">{formData.code} | CIF: {formData.cif}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isEditing && (
            <button
              type="button"
              onClick={() => setShowDeleteDialog(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-destructive text-destructive rounded-lg text-xs hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Eliminar
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/compres')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
          >
            Cancel·lar
          </button>
          {activeTab === 'dades' && (
            <button
              type="submit"
              form="supplier-form"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Desant...' : 'Desa'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Stat cards — solo en edición */}
      {isEditing && (
        <div className="grid grid-cols-4 gap-3">
          <button
            onClick={() => setActiveTab('dades')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'dades' ? 'border-blue-500 bg-blue-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-blue-500/10 shrink-0"><Building2 className="h-4 w-4 text-blue-500" /></div>
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-xs font-medium text-muted-foreground">Dades</span>
              <span className="text-xs font-semibold truncate">Informació</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('comandes')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'comandes' ? 'border-green-500 bg-green-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-green-500/10 shrink-0"><DollarSign className="h-4 w-4 text-green-500" /></div>
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-xs font-medium text-muted-foreground">Comandes</span>
              <span className="text-xs font-semibold">{formatCurrency(totalPurchasesAmount)}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('incidencies')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'incidencies' ? 'border-red-500 bg-red-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-red-500/10 shrink-0"><FileText className="h-4 w-4 text-red-500" /></div>
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-xs font-medium text-muted-foreground">Incidències</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('valoracio')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'valoracio' ? 'border-amber-500 bg-amber-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-amber-500/10 shrink-0"><Star className="h-4 w-4 text-amber-500" /></div>
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-xs font-medium text-muted-foreground">Valoració</span>
              <span className="text-xs font-semibold">{latestEvaluation ? `${latestEvaluation.total?.toFixed(2) || 0}/4` : 'Sense avaluar'}</span>
            </div>
          </button>
        </div>
      )}

      {/* Tab Dades — formulari editable */}
      {(!isEditing || activeTab === 'dades') && (
        <form id="supplier-form" onSubmit={handleSubmit} className="space-y-6 text-[12px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Columna esquerra — Dades Empresa */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-5 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Building2 size={16} className="text-blue-600" /> Dades de l&apos;Empresa
                </h2>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">Codi</label>
                    <input type="text" name="code" value={formData.code} onChange={handleChange}
                      placeholder="Auto (400XXXX) si buit"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Estat</label>
                    <select name="supplierType" value={formData.supplierType} onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                      <option value="HABITUAL">Habitual</option>
                      <option value="ESPORADIC">Esporàdic</option>
                      <option value="PUNTUAL">Puntual</option>
                      <option value="OBSOLET">Obsolet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">CIF *</label>
                    <input type="text" name="cif" value={formData.cif} onChange={handleChange} required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">Raó Social *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Nom Comercial</label>
                    <input type="text" name="tradeName" value={formData.tradeName} onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1">Telèfon General</label>
                    <div className="flex gap-1">
                      <input type="tel" name="phone" value={formData.phone} placeholder="670 435 212"
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
                        className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                      {formData.phone && (
                        <a href={`tel:${formData.phone.replace(/\s/g, '')}`}
                          className="flex items-center px-2 border rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors" title="Trucar">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Email General</label>
                    <div className="flex gap-1">
                      <input type="email" name="email" value={formData.email} onChange={handleChange}
                        className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                      {formData.email && (
                        <a href={`mailto:${formData.email}`}
                          className="flex items-center px-2 border rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors" title="Enviar email">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">
                    <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" />Web</span>
                  </label>
                  <div className="flex gap-1">
                    <input type="url" name="website" value={formData.website} onChange={handleChange}
                      placeholder="https://www.exemple.com"
                      className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                    {formData.website && (
                      <a href={formData.website} target="_blank" rel="noopener noreferrer"
                        className="flex items-center px-2 border rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors" title="Obrir web">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Adreça</label>
                  <input type="text" name="address" value={formData.address} onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div className="grid grid-cols-6 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium mb-1">Ciutat</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium mb-1">Província</label>
                    <input type="text" name="province" value={formData.province} onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">CP</label>
                    <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">País</label>
                    <select name="country" value={formData.country} onChange={handleChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                      {countryCodes.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Columna dreta — Condicions Comercials + Contactes */}
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100">
                  <button type="button" onClick={() => setCondicionsOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                    {condicionsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <CreditCard size={16} className="text-green-600" /> Condicions Comercials
                  </button>
                </div>
                {condicionsOpen && (
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1">Dies de Pagament</label>
                      <input type="number" name="paymentTerms" value={formData.paymentTerms} onChange={handleChange} min={0}
                        className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Mètode de Pagament</label>
                      <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                        <option value="">— Selecciona —</option>
                        {/* Valor antic no present al catàleg actual (p.ex. dades anteriors
                            a la migració al catàleg real) — es mostra tal qual en lloc
                            d'amagar-lo, perque l'usuari vegi que hi ha un valor guardat. */}
                        {formData.paymentMethod && !metodesPagament.some(m => m.name === formData.paymentMethod) && (
                          <option value={formData.paymentMethod}>{formData.paymentMethod}</option>
                        )}
                        {metodesPagament.map(m => (
                          <option key={m.id} value={m.name}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1">IBAN de pagament</label>
                      {bankAccounts.length > 0 ? (
                        <select name="bankAccount" value={formData.bankAccount} onChange={handleChange}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                          <option value="">Selecciona compte...</option>
                          {bankAccounts.map(acc => (
                            <option key={acc.id} value={acc.iban}>
                              {acc.iban.replace(/(.{4})/g, '$1 ').trim()}{acc.bankName ? ` — ${acc.bankName}` : ''}{acc.isDefault ? ' ★' : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input type="text" name="bankAccount" value={formData.bankAccount} onChange={handleChange}
                          placeholder="ES00 0000 0000 0000 0000 0000"
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">IBAN proveïdor</label>
                      <input type="text" name="supplierIban" value={formData.supplierIban} onChange={handleChange}
                        placeholder="ES00 0000 0000 0000 0000 0000"
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1">Forma de Tramesa</label>
                      <select name="shippingMethod" value={formData.shippingMethod} onChange={handleChange}
                        title={SHIPPING_METHODS.find(m => m.value === formData.shippingMethod)?.label || ''}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                        <option value="">Seleccionar forma de tramesa</option>
                        {SHIPPING_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Activitat</label>
                      <select name="activitatId"
                        value={formData.activitatId || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, activitatId: e.target.value ? parseInt(e.target.value, 10) : null }))}
                        title={activitats.find(a => a.id === formData.activitatId)?.name || ''}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                        <option value="">Seleccionar activitat</option>
                        {activitats.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Horari del Proveïdor</label>
                    <input type="text" name="horario" value={formData.horario} onChange={handleChange}
                      placeholder="Ex: Dilluns a Divendres de 9:00 a 18:00"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Notes</label>
                    <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Localització Dropbox</label>
                    <div className="flex gap-1.5">
                      <div className="flex-1 flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/30 text-xs min-h-[34px] overflow-hidden">
                        {formData.dropboxUrl ? (() => {
                          try {
                            const loc: { name: string; link: string } = JSON.parse(formData.dropboxUrl);
                            return (<><FolderOpen className="h-4 w-4 text-blue-500 shrink-0" /><span className="truncate">{loc.name}</span></>);
                          } catch {
                            return <span className="text-muted-foreground truncate">{formData.dropboxUrl}</span>;
                          }
                        })() : <span className="text-muted-foreground">Cap carpeta seleccionada</span>}
                      </div>
                      {dropboxReady && (
                        <button type="button" onClick={handleDropboxChoose}
                          className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors text-xs flex items-center gap-1.5 shrink-0">
                          <FolderOpen className="h-4 w-4" />Seleccionar
                        </button>
                      )}
                      {formData.dropboxUrl && (() => {
                        try {
                          const loc: { name: string; link: string } = JSON.parse(formData.dropboxUrl);
                          return loc.link ? (
                            <a href={loc.link} target="_blank" rel="noopener noreferrer"
                              className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors flex items-center shrink-0" title="Obrir a Dropbox">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          ) : null;
                        } catch {
                          const fileUrl = 'file:///' + formData.dropboxUrl.replace(/\\/g, '/');
                          return (
                            <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                              className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors flex items-center shrink-0 text-muted-foreground hover:text-primary"
                              title="Obrir carpeta">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          );
                        }
                      })()}
                      {formData.dropboxUrl && (
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, dropboxUrl: '' }))}
                          className="px-2 py-2 border rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-red-500 shrink-0">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Contactes compactes */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <button type="button" onClick={() => setContactsOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:opacity-70 transition-opacity">
                    {contactsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Users size={16} className="text-blue-600" /> Persones de Contacte
                    {formData.contacts.length > 0 && (
                      <span className="text-xs font-normal text-muted-foreground">({formData.contacts.length})</span>
                    )}
                  </button>
                  <button type="button" onClick={addContact}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700">
                    <Plus className="h-3 w-3" />Afegir
                  </button>
                </div>
                {contactsOpen && (
                <div className="p-5">
                  {formData.contacts.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">Cap contacte afegit.</p>
                  ) : (
                    <div className="space-y-2 max-h-[220px] overflow-y-auto">
                      {formData.contacts.map((contact, index) => (
                        <div key={index} className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-background">
                          <input type="text" value={contact.name}
                            onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                            placeholder="Nom"
                            className="flex-1 min-w-0 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                          <input type="text" value={contact.position || ''}
                            onChange={(e) => handleContactChange(index, 'position', e.target.value)}
                            placeholder="Càrrec"
                            className="flex-1 min-w-0 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                          <div className="flex gap-1 items-center">
                            <input type="tel" value={contact.phone || ''}
                              onChange={(e) => handleContactChange(index, 'phone', formatPhone(e.target.value))}
                              placeholder="Telèfon"
                              className="w-28 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                            {contact.phone && (
                              <a href={`tel:${contact.phone.replace(/\s/g, '')}`}
                                className="text-muted-foreground hover:text-primary transition-colors" title="Trucar">
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                          <div className="flex gap-1 items-center flex-1 min-w-0">
                            <input type="email" value={contact.email || ''}
                              onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                              placeholder="Email"
                              className="flex-1 min-w-0 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                            {contact.email && (
                              <a href={`mailto:${contact.email}`}
                                className="text-muted-foreground hover:text-primary transition-colors shrink-0" title="Enviar email">
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {contact.isPrimary ? (
                              <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                            ) : (
                              <button type="button" onClick={() => setPrimaryContact(index)}
                                className="p-1 hover:bg-muted rounded" title="Fer principal">
                                <Star className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                            )}
                            <button type="button" onClick={() => removeContact(index)}
                              className="p-1 hover:bg-destructive/10 text-destructive rounded" title="Eliminar">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          </div>
        </form>
      )}

      {/* Tab Comandes */}
      {isEditing && activeTab === 'comandes' && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={16} className="text-purple-600" /> Comandes del Proveïdor
            </h2>
          </div>
          <div className="p-5">
            {loadingOrders ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : orders.length === 0 ? (
              <div className="flex h-32 flex-col items-center justify-center text-muted-foreground">
                <FileText className="mb-2 h-8 w-8" />
                <span className="text-sm">No s&apos;han trobat comandes per a aquest proveïdor</span>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Num. / Comanda</TableHead>
                    <TableHead>Expedient</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead className="text-right">IVA</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-center">Estat</TableHead>
                    <TableHead className="text-right">Accions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.orderId}>
                      <TableCell className="font-mono text-xs">
                        <div>{order.orderNumber}</div>
                        {order.otNumber && <div className="text-muted-foreground">{order.otNumber}</div>}
                      </TableCell>
                      <TableCell className="text-xs">{order.projectNumber || '-'}</TableCell>
                      <TableCell className="text-xs">{formatDate(order.orderDate)}</TableCell>
                      <TableCell className="text-right text-xs">{formatCurrency(order.subtotal)}</TableCell>
                      <TableCell className="text-right text-xs">{formatCurrency(order.taxAmount)}</TableCell>
                      <TableCell className="text-right text-xs font-medium">{formatCurrency(order.total)}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs ${statusConfig[order.status]?.color}`}>
                          {statusConfig[order.status]?.label || order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => navigate(`/compres/comandes/${order.orderId}`)}
                          className="p-2 hover:bg-accent rounded-md" title="Veure comanda">
                          <Eye className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      )}

      {/* Tab Avaluacions */}
      {isEditing && activeTab === 'valoracio' && id && (
        <SupplierEvaluationTab supplierId={id} />
      )}

      {/* Tab Incidències */}
      {isEditing && activeTab === 'incidencies' && id && (
        <SupplierIncidentsTab supplierId={id} />
      )}

      {/* Diàleg eliminar */}
      {isEditing && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Eliminar Proveïdor</DialogTitle>
            </DialogHeader>
            <p className="py-4 text-sm">
              Estàs segur que vols eliminar el proveïdor <strong>{formData.name}</strong>?
              Aquesta acció no es pot desfer.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel·lar</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
